import { ObjectMapper } from "json-object-mapper";
import { IModel } from "services/models/IModel";
import { Model } from "services/models/model";

export class WorkForcePlanJobTitleCountDTO {
	name: string = "";
	count: number = 0;
}

export class WorkforcePlanSummaryScaleModel extends Model implements IModel<WorkforcePlanSummaryScaleModel> {
	name: string = "";
	amountCurrent: WorkForcePlanJobTitleCountDTO[] = [];
	amountVariance: WorkForcePlanJobTitleCountDTO[] = [];
}

export class WorkforcePlanSummaryModel extends Model implements IModel<WorkforcePlanSummaryModel> {
	totalIdeal: number = 0;
	totalAllocated: number = 0;
	totalInTraining: number = 0;
	totalInAptitude: number = 0;
	totalOnLeave: number = 0;
	totalInResignation: number = 0;
	totalPendingHiring: number = 0;
	totalApprentices: number = 0;
	totalTemporary: number = 0;
	maxCapacity: number = 0;
	usedCapacity: number = 0;
	capacityPercentage: number = 0;
	workScales: WorkforcePlanSummaryScaleModel[] = [];
	workScalesGroups: WorkforcePlanSummaryScaleModel[] = [];

	static create(input: any): WorkforcePlanSummaryModel {
		const source = input ?? {};
		const model = ObjectMapper.deserialize<WorkforcePlanSummaryModel>(
			WorkforcePlanSummaryModel,
			source
		);

		// Mapeia escalas manualmente: ObjectMapper não preserva arrays aninhados de forma confiável.
		const groups =
			source.workScalesGroups ??
			source.WorkScalesGroups ??
			source.workScales ??
			source.WorkScales ??
			[];

		model.workScales = WorkforcePlanSummaryModel.mapWorkScales(groups);
		model.workScalesGroups = model.workScales;

		// Garante totais mesmo se o mapper falhar em alguma propriedade nova.
		model.totalInAptitude = WorkforcePlanSummaryModel.readNumber(
			source,
			"totalInAptitude",
			"TotalInAptitude",
			model.totalInAptitude
		);
		model.totalApprentices = WorkforcePlanSummaryModel.readNumber(
			source,
			"totalApprentices",
			"TotalApprentices",
			model.totalApprentices
		);
		model.totalTemporary = WorkforcePlanSummaryModel.readNumber(
			source,
			"totalTemporary",
			"TotalTemporary",
			model.totalTemporary
		);
		model.maxCapacity = WorkforcePlanSummaryModel.readNumber(
			source,
			"maxCapacity",
			"MaxCapacity",
			model.maxCapacity
		);
		model.totalAllocated = WorkforcePlanSummaryModel.readNumber(
			source,
			"totalAllocated",
			"TotalAllocated",
			model.totalAllocated
		);
		model.usedCapacity = WorkforcePlanSummaryModel.readNumber(
			source,
			"usedCapacity",
			"UsedCapacity",
			model.usedCapacity
		);
		model.capacityPercentage = WorkforcePlanSummaryModel.readNumber(
			source,
			"capacityPercentage",
			"CapacityPercentage",
			model.capacityPercentage
		);

		return model;
	}

	private static mapWorkScales(groups: any): WorkforcePlanSummaryScaleModel[] {
		if (!Array.isArray(groups)) {
			return [];
		}

		return groups.map((group: any) => {
			const scale = new WorkforcePlanSummaryScaleModel();
			scale.name = group?.name ?? group?.Name ?? "";
			scale.amountCurrent = WorkforcePlanSummaryModel.mapJobTitleCounts(
				group?.amountCurrent ?? group?.AmountCurrent
			);
			scale.amountVariance = WorkforcePlanSummaryModel.mapJobTitleCounts(
				group?.amountVariance ?? group?.AmountVariance
			);
			return scale;
		});
	}

	private static mapJobTitleCounts(items: any): WorkForcePlanJobTitleCountDTO[] {
		if (!Array.isArray(items)) {
			return [];
		}

		return items.map((item: any) => {
			const count = new WorkForcePlanJobTitleCountDTO();
			count.name = item?.name ?? item?.Name ?? "";
			count.count = Number(item?.count ?? item?.Count ?? 0);
			return count;
		});
	}

	private static readNumber(
		source: any,
		camelKey: string,
		pascalKey: string,
		fallback: number
	): number {
		const value = source?.[camelKey] ?? source?.[pascalKey];
		return typeof value === "number" && !Number.isNaN(value) ? value : fallback ?? 0;
	}

	public toJSON(): string {
		return ObjectMapper.serialize(this) as string;
	}
}
